/**
 * Google CMP Component for GDPR regions (EEA, UK, Switzerland)
 * Uses Google's Privacy & messaging tool which is IAB TCF certified
 *
 * This component integrates with Google's consent management platform
 * to comply with GDPR requirements for serving personalized ads.
 */
import React, { useEffect, useState } from 'react';
import { logger } from "../../utils/logger";
import { ADSENSE_CLIENT_ID } from '../../../shared/adsense/adSurfacePolicy';
// import { logger } from "../../utils/logger";
// Extend Window interface to include Google consent functions
declare global {
    interface Window {
        gtag: any;
        dataLayer: any[];
    }
}
interface GoogleCMPProps {
    onConsentLoaded?: () => void;
}
/**
 * Google CMP Component
 * This uses Google's built-in consent management which is TCF-certified
 */
const GoogleCMP: React.FC<GoogleCMPProps> = ({ onConsentLoaded }) => {
    const [isLoaded, setIsLoaded] = useState(false);
    useEffect(() => {
        // Google CMP now only manages consent, not GA loading
        // GA is loaded by the GoogleAnalytics component with proper measurement ID
        // Just ensure gtag/dataLayer exist (should be set in index.html)
        if (!window.dataLayer) {
            window.dataLayer = [];
        }
        if (!window.gtag) {
            window.gtag = function () {
                window.dataLayer.push(arguments);
            };
        }
        // CRITICAL: For EEA users, immediately set consent to 'denied'
        // This overrides the default 'granted' consent set for non-EEA users in index.html
        if (window.gtag) {
            window.gtag('consent', 'update', {
                'analytics_storage': 'denied',
                'ad_storage': 'denied',
                'ad_user_data': 'denied',
                'ad_personalization': 'denied',
            });
            if (process.env.NODE_ENV === 'development') {
                logger.debug('🏛️ Google CMP: Consent set to DENIED for GDPR region');
            }
        }
        // Configure AdSense with consent mode (no need to load full GA)
        // AdSense ads will use the same gtag as main GA tracking
        if (window.gtag) {
            window.gtag('config', ADSENSE_CLIENT_ID, {
                'privacy_sandbox': true
            });
        }
        if (process.env.NODE_ENV === 'development') {
            logger.debug('🏛️ Google CMP ready (consent-only, no duplicate GA loading)');
        }
        setIsLoaded(true);
        onConsentLoaded?.();
        // Listen for consent updates
        const handleConsentUpdate = (event: Event) => {
            const customEvent = event as CustomEvent;
            if (process.env.NODE_ENV === 'development') {
                logger.debug('🏛️ Google CMP consent updated:', { "arg0": customEvent.detail });
            }
        };
        window.addEventListener('consent_update', handleConsentUpdate);
        return () => {
            window.removeEventListener('consent_update', handleConsentUpdate);
        };
    }, [onConsentLoaded]);
    // This component doesn't render any visible UI
    // The consent banner is automatically managed by Google
    return null;
};
export default GoogleCMP;
/**
 * Helper function to manually show Google's consent form
 * Can be called from cookie settings link
 */
export function showGoogleConsentForm(): void {
    // Google's consent form can be triggered via gtag command
    if (window.gtag) {
        // Trigger the consent form to show
        window.gtag('consent', 'update', {
            'ad_storage': 'denied',
            'analytics_storage': 'denied',
            'ad_user_data': 'denied',
            'ad_personalization': 'denied'
        });
        // Then immediately show the consent UI
        // Note: Google's Privacy & messaging tool automatically shows the banner
        // on first visit for GDPR regions
    }
}
/**
 * Check if user has provided consent via Google CMP
 */
export function hasGoogleConsent(): boolean {
    try {
        // Check if consent has been stored
        const consentCookie = document.cookie
            .split('; ')
            .find(row => row.startsWith('_gcl_consent='));
        if (consentCookie) {
            return consentCookie.includes('granted');
        }
        // Also check localStorage for consent state
        const storedConsent = localStorage.getItem('google_consent_state');
        if (storedConsent) {
            const consentState = JSON.parse(storedConsent);
            return consentState.granted === true;
        }
    }
    catch (error) {
        logger.error('Error checking Google consent:', error, {});
    }
    return false;
}
